import { PMS_SETTING_KEYS, PMS_SETTING_LIMITS, type PmsSettingsValues } from '@felix/domain';
import { Alert, Button, NumberInput, Select, Skeleton } from '@mantine/core';
import { IconAlertCircle, IconListTree, IconRefresh, IconTool } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import type { PmsSettings } from '../pms/condition-types.js';
import { usePmsErrorText } from '../pms/EquipmentDrawer.js';
import type { PmsSource } from '../pms/source.js';
import { CardFooter, Fact, Facts, settingsClasses } from './SettingsLayout.js';

const LABEL: Readonly<Record<keyof PmsSettingsValues, [string, string]>> = {
  dueSoonDays: ['dueSoonDays', 'dueSoonHint'],
  lateEntryDays: ['lateEntryDays', 'lateEntryHint'],
  certificateExpiringDays: ['certificateExpiringDays', 'certificateHint'],
};

export interface PmsStructure {
  /** Varsayılan iskeletin yüklenebileceği gemiler: ekipmansız (gemide ilk çekmeden sonra). */
  vessels: readonly { id: string; name: string }[];
  onLoad: (vesselId: string) => Promise<{ created: number }>;
}

export interface PmsSettingsPanelProps {
  scope: 'web' | 'ship';
  /** `null`: okunuyor ya da okunamadı. */
  settings: PmsSettings | null;
  error?: string | null;
  onRetry?: () => void;
  /** Ofiste "Ayarlar / düzenleme" yetkisi varsa (şirket ayarı). */
  onSave?: (rowVersion: number | null, values: Partial<PmsSettingsValues>) => Promise<void>;
  structure?: PmsStructure | null;
}

/**
 * Settings ▸ PMS bölümü (plan M7 Task 21c; K27, K30, K32): Due Soon penceresi, geç giriş eşiği ve
 * sertifika "Expiring" eşiği; varsayılan ekipman yapısı (Task 4). Web'de düzenlenir (şirket
 * ayarı); gemide salt okunurdur, yalnız iskelet düğmesi gemide çalışır. Uyum kuralları ayar olarak
 * görünmez [NR496 1.4.2].
 */
export function PmsSettingsPanel({
  scope,
  settings,
  error,
  onRetry,
  onSave,
  structure,
}: PmsSettingsPanelProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.settings.${key}`, options);
  const errorText = usePmsErrorText();
  const [values, setValues] = useState<Partial<Record<keyof PmsSettingsValues, number | ''>>>(() =>
    settings
      ? {
          dueSoonDays: settings.dueSoonDays,
          lateEntryDays: settings.lateEntryDays,
          certificateExpiringDays: settings.certificateExpiringDays,
        }
      : {},
  );
  const [failure, setFailure] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vessel, setVessel] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ayar sürümü değişince kurulur
  useEffect(() => {
    if (!settings) return;
    setValues({
      dueSoonDays: settings.dueSoonDays,
      lateEntryDays: settings.lateEntryDays,
      certificateExpiringDays: settings.certificateExpiringDays,
    });
  }, [settings?.rowVersion, settings?.dueSoonDays]);
  const editable = scope === 'web' && Boolean(onSave);
  const changed =
    settings !== null &&
    PMS_SETTING_KEYS.some((key) => values[key] !== undefined && values[key] !== settings[key]);
  const invalid = PMS_SETTING_KEYS.some((key) => values[key] === '');
  const candidates = structure?.vessels ?? [];
  const selected = vessel ?? candidates[0]?.id ?? null;

  const body = (() => {
    if (error) {
      return (
        <EmptyState
          tone="danger"
          title={t('pms.work.loadError')}
          description={errorText({ code: error })}
          action={
            onRetry ? (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={onRetry}
              >
                {t('pms.equipment.retry')}
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (!settings) {
      return (
        <div aria-busy="true">
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} />
        </div>
      );
    }
    return (
      <>
        <p className={settingsClasses.intro}>{k('intro')}</p>
        <Facts>
          {PMS_SETTING_KEYS.map((key) => {
            const [label, hint] = LABEL[key];
            const limit = PMS_SETTING_LIMITS[key];
            return (
              <Fact key={key} label={k(label)} help={k(hint)}>
                {editable ? (
                  <NumberInput
                    className={settingsClasses.select}
                    aria-label={k(label)}
                    min={limit.min}
                    max={limit.max}
                    allowDecimal={false}
                    clampBehavior="strict"
                    rightSection={<span className={settingsClasses.unit}>{k('days')}</span>}
                    rightSectionWidth={44}
                    value={values[key] ?? ''}
                    onChange={(value) => {
                      setSaved(false);
                      setValues((current) => ({
                        ...current,
                        [key]: typeof value === 'number' ? value : '',
                      }));
                    }}
                  />
                ) : (
                  `${settings[key]} ${k('days')}`
                )}
              </Fact>
            );
          })}
        </Facts>
        {scope === 'ship' ? <p className={settingsClasses.note}>{k('readOnly')}</p> : null}
        {failure ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
            {errorText(failure)}
          </Alert>
        ) : saved ? (
          <Alert color="green" role="status" mt="sm">
            {k('saved')}
          </Alert>
        ) : null}
        {editable ? (
          <CardFooter>
            <Button
              loading={busy}
              disabled={!changed || invalid}
              onClick={async () => {
                if (!onSave) return;
                setBusy(true);
                setFailure(null);
                try {
                  const patch: Partial<PmsSettingsValues> = {};
                  for (const key of PMS_SETTING_KEYS) {
                    const value = values[key];
                    if (typeof value === 'number' && value !== settings[key]) patch[key] = value;
                  }
                  await onSave(settings.rowVersion, patch);
                  setSaved(true);
                } catch (caught) {
                  setFailure(caught);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {k('save')}
            </Button>
          </CardFooter>
        ) : null}
      </>
    );
  })();

  return (
    <Panel title={k('title')} icon={<IconTool size={18} stroke={1.8} />} divider>
      {body}
      {structure ? (
        <div className={settingsClasses.section}>
          <h3 className={settingsClasses.sectionTitle}>
            <IconListTree size={16} stroke={1.8} aria-hidden />
            {k('structure')}
          </h3>
          <p className={settingsClasses.note}>{k('structureHint')}</p>
          {candidates.length === 0 ? (
            <p className={settingsClasses.note}>{k('structureNone')}</p>
          ) : (
            <div className={settingsClasses.inline}>
              {scope === 'web' ? (
                <Select
                  className={settingsClasses.select}
                  aria-label={k('structureVessel')}
                  data={candidates.map((v) => ({ value: v.id, label: v.name }))}
                  value={selected}
                  allowDeselect={false}
                  onChange={setVessel}
                />
              ) : null}
              <Button
                variant="default"
                loading={loading}
                disabled={!selected}
                onClick={async () => {
                  if (!selected) return;
                  setLoading(true);
                  setLoadError(null);
                  try {
                    setLoaded((await structure.onLoad(selected)).created);
                  } catch (caught) {
                    setLoadError(caught);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {k('load')}
              </Button>
            </div>
          )}
          {loadError ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
              {errorText(loadError)}
            </Alert>
          ) : loaded !== null ? (
            <Alert color="green" role="status" mt="sm">
              {k('structureLoaded', { count: loaded })}
            </Alert>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Bölümün veri bağlantısı: ayarlar ve iskelet adayları PMS kaynağından (web: kurulum devrindeki
 * ekipmansız gemiler; gemi: ekipmanı yoksa kendi gemisi).
 */
export function PmsSettingsSection({ source }: { source: PmsSource }) {
  const [settings, setSettings] = useState<PmsSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<{ id: string; name: string }[] | null>(null);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` yeniden okutur
  useEffect(() => {
    if (!source.settings) return;
    let live = true;
    setError(null);
    source
      .settings()
      .then((value) => {
        if (live) setSettings(value);
      })
      .catch((caught: unknown) => {
        const code = (caught as { code?: unknown } | null)?.code;
        if (live) setError(typeof code === 'string' ? code : 'GENERIC');
      });
    return () => {
      live = false;
    };
  }, [source, attempt]);
  useEffect(() => {
    if (!source.editor?.loadDefaultStructure) return;
    let live = true;
    (async () => {
      const vessels = await source.vessels();
      const out: { id: string; name: string }[] = [];
      for (const vessel of vessels) {
        const tree = await source.tree(vessel.id).catch(() => null);
        // Ofiste yalnız kurulum devrindeki gemi (K1); gemide kendi gemisi
        if (!tree || tree.nodes.length > 0) continue;
        if (source.scope === 'web' && tree.writer !== 'cloud') continue;
        if (!tree.can.create) continue;
        out.push({ id: vessel.id, name: vessel.name });
      }
      if (live) setCandidates(out);
    })().catch(() => {
      if (live) setCandidates([]);
    });
    return () => {
      live = false;
    };
  }, [source]);
  if (!source.settings) return null;
  const save = source.settingsEditor?.save;
  const load = source.editor?.loadDefaultStructure;
  return (
    <PmsSettingsPanel
      scope={source.scope}
      settings={settings}
      error={error}
      onRetry={() => setAttempt((n) => n + 1)}
      {...(save
        ? {
            onSave: async (rowVersion, values) => {
              setSettings(await save(rowVersion, values));
            },
          }
        : {})}
      structure={
        load && candidates
          ? {
              vessels: candidates,
              onLoad: async (vesselId) => {
                const result = await load(vesselId);
                setCandidates((list) => (list ?? []).filter((v) => v.id !== vesselId));
                return result;
              },
            }
          : null
      }
    />
  );
}
